import React, { useState, useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRightIcon, ArrowLeftIcon, XIcon, SparklesIcon, MonitorIcon, MailIcon, ArrowUpRightIcon, MenuIcon } from "lucide-react";

// --- Types & Data ---
type ProjectType = 'website';
interface TourStep {
  title: string;
  description: string;
  x: number;
  y: number;
}
interface Project {
  id: string;
  name: string;
  type: ProjectType;
  description: string;
  tags: string[];
  screenshot?: string;
  liveUrl?: string;
  accent: string;
  tour?: TourStep[];
}
const projects: Project[] = [{
  id: 'teri-britton',
  name: 'Teri Britton',
  type: 'website',
  description: 'A polished coastal real-estate experience that welcomes visitors into Teri Britton’s client-first approach.',
  tags: ['Real Estate', 'Luxury', 'Personal Brand'],
  screenshot: 'https://cdn.magicpatterns.com/uploads/jfVmPJM4LyP3Y8ADWDHwWw/file_00000000571c81f586b27c328133e983.png',
  accent: 'from-sky-100 via-white to-stone-100',
  liveUrl: 'https://www.teribritton.com'
}, {
  id: 'theepinc',
  name: 'The EP Inc',
  type: 'website',
  description: 'A professional, modern web presence built to establish trust and clearly communicate core services.',
  tags: ['Corporate', 'Business', 'Professional'],
  screenshot: 'https://cdn.magicpatterns.com/uploads/ujw8ujVYeEtXbZe7wA3WJy/image.png',
  accent: 'from-blue-100 via-white to-slate-200',
  liveUrl: 'https://theepinc.com'
}, {
  id: 'wonders-of-life',
  name: 'Wonders of Life Spa',
  type: 'website',
  description: 'A serene, luxury wellness-spa site designed to feel like the experience itself. Soft lavender palette and atmospheric photography.',
  tags: ['Wellness', 'Luxury', 'Brand Site'],
  screenshot: 'https://cdn.magicpatterns.com/uploads/1xVZ4cruGbPcAxeoaZbD7W/image.png',
  accent: 'from-purple-100 via-white to-lavender-200',
  liveUrl: 'https://project-wondersoflife.magicpatterns.app/'
}, {
  id: 'twm-main',
  name: 'Trippin With Mandy',
  type: 'website',
  description: 'The central hub for all things Mandy. A vibrant, personality-driven portfolio showcasing web design, virtual tours, and creative services.',
  tags: ['Portfolio', 'Personal Brand', 'Creative'],
  screenshot: 'https://cdn.magicpatterns.com/uploads/xhjS3MxsEAmrkrH178kosY/image.png',
  accent: 'from-pink-200 via-rose-100 to-purple-200',
  liveUrl: 'https://trippinwithmandy.com'
}, {
  id: 'twm-agency',
  name: 'TWM Agency',
  type: 'website',
  description: 'A sleek, professional agency front for larger-scale projects. Dark mode aesthetic with neon accents for a modern, high-end feel.',
  tags: ['Agency', 'B2B', 'Dark Mode'],
  screenshot: 'https://cdn.magicpatterns.com/uploads/frLAmDHVoLi1J4ZtDs2fR3/image.png',
  accent: 'from-blue-200 via-indigo-100 to-purple-200',
  liveUrl: 'https://trippinwithmandy.com/agency'
}];

// --- In-Screen Menu Component ---
function InScreenMenu({
  onSelectTour,
  onSelectLive,
  onClose
}: {
  onSelectTour: (p: Project) => void;
  onSelectLive: (p: Project) => void;
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 flex flex-col items-center justify-start overflow-y-auto overflow-x-hidden bg-zinc-950 pt-24 sm:p-10 sm:pt-32" data-id="element-0" data-mp="yflf57oatx5s">
      <button onClick={onClose} className="absolute top-16 right-4 sm:top-20 sm:right-6 z-50 rounded-full bg-white/10 p-2 text-white/70 backdrop-blur-md transition-colors hover:bg-white/20 hover:text-white" aria-label="Close menu" data-id="element-1" data-mp="1r4sf8b3eiut5">
        <XIcon className="h-5 w-5" data-id="element-2" data-mp="1m4egjjnowx7s" />
      </button>

      <h2 className="font-[DM_Serif_Display] text-3xl sm:text-5xl text-white mb-2 drop-shadow-md shrink-0 px-4 sm:px-0 text-center" data-id="element-3" data-mp="1m791dcam8k1q">Portfolio</h2>
      <p className="text-white/60 text-sm sm:text-base mb-8 shrink-0 px-4 sm:px-0 text-center" data-id="element-4" data-mp="aq9a8k008b9a">
        Select a project to explore
      </p>
      
      <div className="grid w-full max-w-4xl shrink-0 gap-6 px-4 pb-12 md:grid-cols-2" data-id="element-5" data-mp="qz8i1fp3q5v1">
        {projects.map(p => {
        const hasLive = !!p.liveUrl;
        const hasTour = !!p.tour && p.tour.length > 0;
        return <div key={p.id} className="group relative overflow-hidden rounded-xl bg-white/5 border border-white/10 text-left transition duration-300 hover:bg-white/10 hover:border-white/30 hover:shadow-[0_0_30px_rgba(255,255,255,0.1)]" data-id="element-6" data-mp="c90w2pzvy4pl">
              <div className="aspect-[16/10] w-full overflow-hidden" data-id="element-7" data-mp="zxk557qreliq">
                <img src={p.screenshot} alt={p.name} className="h-full w-full object-cover object-top opacity-60 transition duration-700 group-hover:scale-105 group-hover:opacity-100" data-id="element-8" data-mp="zzwui5yujy5t" />
              </div>
              <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/95 via-black/60 to-transparent p-5 sm:p-6" data-id="element-9" data-mp="3klq0zmef9i5y">
                <h3 className="font-[DM_Serif_Display] text-2xl text-white leading-tight" data-id="element-10" data-mp="1w583f5lxr2w8">{p.name}</h3>
                <p className="text-white/70 text-sm mt-2 line-clamp-2" data-id="element-11" data-mp="2071zf0l6riq2">{p.description}</p>
                <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-12" data-mp="254v23jna5ull">
                  {hasTour && <button onClick={() => onSelectTour(p)} className="inline-flex items-center gap-2 rounded-full bg-amber-500/20 border border-amber-500/50 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-400 transition-colors hover:bg-amber-500 hover:text-white" data-id="element-13" data-mp="29wv91w1rpnro">
                      <SparklesIcon className="h-3.5 w-3.5" data-id="element-14" data-mp="2ntrzvxhvz58k" /> Take Tour
                    </button>}
                  {hasLive && <button onClick={() => onSelectLive(p)} className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-zinc-900" data-id="element-15" data-mp="timwmuapoutr">
                      <MonitorIcon className="h-3.5 w-3.5" data-id="element-16" data-mp="1wuk6n4gg0lds" /> Live Site
                    </button>}
                </div>
              </div>
            </div>;
      })}
      </div>
    </motion.div>;
}

// --- In-Screen Live Preview Component ---
function InScreenLivePreview({
  project,
  onClose
}: {
  project: Project;
  onClose: () => void;
}) {
  if (!project.liveUrl) return null;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 flex flex-col bg-zinc-950" data-id="element-17" data-mp="2tiqhi93yka0i">
      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between bg-gradient-to-b from-black/90 to-transparent p-4 pt-16 sm:p-6 sm:pt-20 pointer-events-none" data-id="element-18" data-mp="33gxpi2g9rhqd">
         <div className="flex items-center gap-3 pointer-events-auto" data-id="element-19" data-mp="2yg083s332rpx">
           <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-white/20 border border-white/50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-md" data-id="element-20" data-mp="3i463fb0yq161">
             <MonitorIcon size={12} data-id="element-21" data-mp="385762olndryc" /> Live Site
           </span>
           <h2 className="font-[DM_Serif_Display] text-xl sm:text-2xl text-white drop-shadow-md" data-id="element-22" data-mp="3d3iecuwanueg">{project.name}</h2>
         </div>
         <button onClick={onClose} className="pointer-events-auto rounded-full bg-black/50 border border-white/20 p-2 text-white hover:bg-white/20 backdrop-blur-md transition-colors" data-id="element-23" data-mp="2zbu30ttvd9yj">
           <XIcon size={18} data-id="element-24" data-mp="2pgacyo1zl5hp" />
         </button>
      </div>

      {/* Iframe Area */}
      <div className="relative h-full w-full bg-white pt-28 sm:pt-32" data-id="element-25" data-mp="2uf3pad5jeqga">
        <iframe src={project.liveUrl} title={`${project.name} live preview`} className="h-full w-full border-0" sandbox="allow-scripts allow-same-origin allow-forms allow-popups" data-id="element-26" data-mp="2gej7nkbcz0od" />
      </div>
    </motion.div>;
}

// --- In-Screen Tour Component ---
function InScreenTour({
  project,
  onClose
}: {
  project: Project;
  onClose: () => void;
}) {
  const [activeStep, setActiveStep] = useState(0);
  const totalSteps = project.tour?.length ?? 0;
  const next = useCallback(() => setActiveStep(s => Math.min(s + 1, totalSteps - 1)), [totalSteps]);
  const prev = useCallback(() => setActiveStep(s => Math.max(s - 1, 0)), []);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, next, prev]);
  if (!project.tour) return null;
  const step = project.tour[activeStep];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 flex flex-col bg-zinc-950" data-id="element-27" data-mp="1u770ecvogcrx">
      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between bg-gradient-to-b from-black/90 to-transparent p-4 pt-16 sm:p-6 sm:pt-20" data-id="element-28" data-mp="dq7vxuripnjn">
         <div className="flex items-center gap-3" data-id="element-29" data-mp="inhuq10srbk5">
           <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-amber-500/20 border border-amber-500/50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-400 backdrop-blur-md" data-id="element-30" data-mp="noxhpymjbs56">
             <MonitorIcon size={12} data-id="element-31" data-mp="3r65x8btsifwq" /> Interactive Tour
           </span>
           <h2 className="font-[DM_Serif_Display] text-xl sm:text-2xl text-white drop-shadow-md" data-id="element-32" data-mp="3w3yzwuvvwrs9">{project.name}</h2>
         </div>
         <button onClick={onClose} className="rounded-full bg-black/50 border border-white/20 p-2 text-white hover:bg-white/20 backdrop-blur-md transition-colors" data-id="element-33" data-mp="4ql2pw8xzo1o">
           <XIcon size={18} data-id="element-34" data-mp="38ed0q7fiick9" />
         </button>
      </div>

      {/* Screenshot Area */}
      <div className="relative h-full w-full overflow-hidden" data-id="element-35" data-mp="2f7dkynnr32n8">
        <img src={project.screenshot} alt={project.name} className="h-full w-full object-cover object-top opacity-80" data-id="element-36" data-mp="2f6vdsvzjv40j" />
        <div className="absolute inset-0 bg-black/30" data-id="element-37" data-mp="2k818gzcrt1cp" />

        {/* Active Hotspot */}
        <AnimatePresence mode="wait" data-id="element-38" data-mp="2p38c6761i61m">
          <motion.div key={activeStep} initial={{
          opacity: 0,
          scale: 0.6
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.6
        }} transition={{
          duration: 0.3
        }} className="absolute z-20" style={{
          left: `${step.x}%`,
          top: `${step.y}%`,
          transform: 'translate(-50%, -50%)'
        }} data-id="element-39" data-mp="2u4xjbbq55zkf">
            <div className="relative" data-id="element-40" data-mp="2b59xt1rpy9m4">
              <motion.div animate={{
              scale: [1, 2.5, 1],
              opacity: [0.6, 0, 0.6]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeOut'
            }} className="absolute inset-0 h-8 w-8 sm:h-12 sm:w-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-500" data-id="element-41" data-mp="2b6bje06d2ei3" />
              <motion.div animate={{
              scale: [1, 1.8, 1],
              opacity: [0.8, 0, 0.8]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeOut',
              delay: 0.4
            }} className="absolute inset-0 h-8 w-8 sm:h-12 sm:w-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-400" data-id="element-42" data-mp="3vxeg1mxe96pp" />
              <div className="relative flex h-8 w-8 sm:h-12 sm:w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[3px] border-white bg-amber-500 text-xs sm:text-base font-bold text-white shadow-xl" data-id="element-43" data-mp="3d0ed92r84o1h">
                {activeStep + 1}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Inactive Hotspots */}
        {project.tour.map((s, i) => {
        if (i === activeStep) return null;
        return <button key={i} onClick={() => setActiveStep(i)} className="group absolute z-10" style={{
          left: `${s.x}%`,
          top: `${s.y}%`,
          transform: 'translate(-50%, -50%)'
        }} data-id="element-44" data-mp="2pbbwdx4eyz81" data-mpi={i}>
              <div className="flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center rounded-full border-2 border-amber-500 bg-black/60 text-xs sm:text-sm font-bold text-amber-400 shadow-md backdrop-blur-md transition-all group-hover:scale-110 group-hover:bg-amber-500 group-hover:text-white" data-id="element-45" data-mp="2knc50wc9as4p">
                {i + 1}
              </div>
            </button>;
      })}
      </div>

      {/* Bottom Control Panel */}
      <div className="absolute bottom-0 left-0 right-0 z-30 bg-black/80 backdrop-blur-xl border-t border-white/10 p-4 sm:p-6 sm:px-8" data-id="element-46" data-mp="33f4x8oji49bd">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-47" data-mp="2tmpgfujxxqs2">
          <div className="flex-1" data-id="element-48" data-mp="3hd1y3x4zn1vu">
            <div className="flex items-center gap-2 mb-2" data-id="element-49" data-mp="3hc09epv1ugvn">
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-amber-500" data-id="element-50" data-mp="3m39xhm0y9c5f">Step {activeStep + 1} of {totalSteps}</span>
              <div className="flex items-center gap-1 ml-2" data-id="element-51" data-mp="1amykqu1jx5u3">
                {project.tour.map((_, i) => <div key={i} className={`h-1.5 rounded-full transition-all ${i === activeStep ? 'w-6 bg-amber-500' : 'w-1.5 bg-white/20'}`} data-id="element-52" data-mp="15p6ip77703c6" data-mpi={i} />)}
              </div>
            </div>
            <AnimatePresence mode="wait" data-id="element-53" data-mp="1fg1h19xpqtdz">
              <motion.div key={activeStep} initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-54" data-mp="rpf71d45ez1c">
                <h3 className="font-[DM_Serif_Display] text-xl sm:text-2xl text-white mb-1" data-id="element-55" data-mp="11ls14c3lvewm">{step.title}</h3>
                <p className="text-xs sm:text-sm text-white/70 line-clamp-2 sm:line-clamp-none" data-id="element-56" data-mp="wr4md4yelyv8">{step.description}</p>
              </motion.div>
            </AnimatePresence>
          </div>
          
          <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto" data-id="element-57" data-mp="2869qmde6o5gy">
            <button onClick={prev} disabled={activeStep === 0} className="flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-30 transition-colors" data-id="element-58" data-mp="hoo02t582yg9">
              <ArrowLeftIcon size={20} data-id="element-59" data-mp="vo5v3q9437y2" />
            </button>
            <button onClick={next} disabled={activeStep === totalSteps - 1} className="flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-30 transition-colors shadow-[0_0_15px_rgba(245,158,11,0.4)]" data-id="element-60" data-mp="10m7eyded3bal">
              <ArrowRightIcon size={20} data-id="element-61" data-mp="1eckmwfr96pzx" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>;
}

// --- Privacy Policy Dialog ---
function PrivacyDialog({
  onClose
}: {
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-[400] flex items-center justify-center bg-black/80 p-4 sm:p-6 backdrop-blur-md" role="dialog" aria-modal="true" aria-labelledby="privacy-title" data-id="element-62" data-mp="1o8xg7civz1u5">
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} transition={{
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative flex h-full max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-zinc-900 shadow-2xl" data-id="element-63" data-mp="1kgqt9mhqeyk4">
        <div className="flex items-center justify-between border-b border-white/10 bg-zinc-900/80 px-6 py-4 backdrop-blur-sm" data-id="element-64" data-mp="23dia7p9sx1l0">
          <h2 id="privacy-title" className="font-[DM_Serif_Display] text-2xl text-white" data-id="element-65" data-mp="2zyaibkkqqezr">Privacy Policy</h2>
          <button onClick={onClose} className="inline-flex size-8 items-center justify-center rounded-full bg-white/10 text-white/70 transition-colors hover:bg-white/20 hover:text-white" aria-label="Close privacy dialog" data-id="element-66" data-mp="2v3mod66ywsds">
            <XIcon size={18} data-id="element-67" data-mp="jhkt8ycpkckl" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 text-sm leading-relaxed text-white/80 sm:p-8 sm:text-base" data-id="element-68" data-mp="og5ki89zflww">
          <div className="mx-auto max-w-3xl space-y-6" data-id="element-69" data-mp="agmr6lltyf4n">
            <p className="font-bold text-white" data-id="element-70" data-mp="fg93mstu3rce">Websites by Mandy — Privacy Policy</p>
            <p className="text-xs text-white/50" data-id="element-71" data-mp="fe667nqcqvxn">Last Updated: August 15, 2026</p>
            <p data-id="element-72" data-mp="lcnzujp41pa">Websites by Mandy respects your privacy and is committed to protecting the personal information you provide when visiting our website, contacting us, or purchasing our services.</p>
            <p data-id="element-73" data-mp="1uy2k8ixjdmzu">This Privacy Policy explains what information we may collect, how we use it, when it may be shared, and the choices you have regarding your information.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-74" data-mp="3kf6e5w6xopy0">1. Information We May Collect</h3>
            <p data-id="element-75" data-mp="3fgwilkharnbv">When you visit Websites by Mandy or contact us about our services, we may collect information that you voluntarily provide, including:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-76" data-mp="3pdrf5xu0fdni">
              <li data-id="element-77" data-mp="3pc6k873s9sp4">Your name</li>
              <li data-id="element-78" data-mp="6zi6dcox0mvd">Email address</li>
              <li data-id="element-79" data-mp="6ygvp9bta2e6">Phone number</li>
              <li data-id="element-80" data-mp="6xp2241ryeqj">Business or company name</li>
              <li data-id="element-81" data-mp="2yg6qur368sv">Information about your business or website project</li>
              <li data-id="element-82" data-mp="gxo7hs2wdoz6">Messages or information submitted through contact or inquiry forms</li>
              <li data-id="element-83" data-mp="gvbt1owclwqv">Files, photographs, logos, written content, or other materials you provide for your website project</li>
              <li data-id="element-84" data-mp="2iosw0f9ja9fa">Billing or transaction information related to services you purchase</li>
              <li data-id="element-85" data-mp="2dpy6kuh29mm7">Any other information you voluntarily provide to us</li>
            </ul>
            <p data-id="element-86" data-mp="2dsax3w0vx5av">We only ask for information that is reasonably necessary to communicate with you or provide the services you request.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-87" data-mp="1wv3pebuax6w0">2. Information Collected Automatically</h3>
            <p data-id="element-88" data-mp="21nwu4zprsf83">When you visit our website, certain technical information may be collected automatically by our website, hosting provider, analytics services, or other technology providers.</p>
            <p data-id="element-89" data-mp="18yzphzehegxt">This information may include:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-90" data-mp="190au21fr3spz">
              <li data-id="element-91" data-mp="1dxsrovobilio">IP address</li>
              <li data-id="element-92" data-mp="1dz3y7395e7dh">Browser type</li>
              <li data-id="element-93" data-mp="1ivkwgqa2a24a">Device type</li>
              <li data-id="element-94" data-mp="1ivb631l3tsy2">Operating system</li>
              <li data-id="element-95" data-mp="1iygsua69giqm">Pages visited</li>
              <li data-id="element-96" data-mp="1nvgywl341lhq">Approximate geographic location</li>
              <li data-id="element-97" data-mp="1nt4bhsel2ixa">Referring website</li>
              <li data-id="element-98" data-mp="2uc2h7t4aqgb3">Date and time of visits</li>
              <li data-id="element-99" data-mp="2uaztfu29stze">General website usage and interaction information</li>
            </ul>
            <p data-id="element-100" data-mp="2z8swwfc6v9vz">This information may be used to understand how visitors use our website, improve website performance, troubleshoot problems, maintain security, and improve our services.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-101" data-mp="ip8pc74pdwq1">3. How We Use Your Information</h3>
            <p data-id="element-102" data-mp="dvmk300vcv49">Websites by Mandy may use information we collect to:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-103" data-mp="8x2zp41xbre0">
              <li data-id="element-104" data-mp="8w9z6ktk5y4b">Respond to questions and website inquiries</li>
              <li data-id="element-105" data-mp="40kz8gdmmf7h">Communicate with potential and existing clients</li>
              <li data-id="element-106" data-mp="3w2khik4fs1is">Provide quotes or information about our services</li>
              <li data-id="element-107" data-mp="3r3pqww0898o4">Design, develop, and complete website projects</li>
              <li data-id="element-108" data-mp="3r3zg4hdg7bsr">Process and maintain records of transactions</li>
              <li data-id="element-109" data-mp="3m5o8mdamlddp">Provide customer support</li>
              <li data-id="element-110" data-mp="3m6zag7kigb26">Deliver completed projects</li>
              <li data-id="element-111" data-mp="1pbba609f6a9a">Improve our website, services, and customer experience</li>
              <li data-id="element-112" data-mp="1kdja2iymfhu0">Prevent fraud, abuse, or security issues</li>
              <li data-id="element-113" data-mp="1kb6rq4oyb5gf">Maintain necessary business and financial records</li>
              <li data-id="element-114" data-mp="2c616jtpie09u">Comply with applicable legal requirements</li>
            </ul>
            <p data-id="element-115" data-mp="277zmb5gcjuws">Websites by Mandy does not sell your personal information.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-116" data-mp="22h9i3vjnpy3o">4. Payments</h3>
            <p data-id="element-117" data-mp="1xig5s6g3wd53">If you purchase services from Websites by Mandy, payments may be processed through a third-party payment provider.</p>
            <p data-id="element-118" data-mp="1thw4qrsewpdo">Websites by Mandy may receive information confirming your transaction, such as your name, payment amount, transaction status, and other information necessary for our business records.</p>
            <p data-id="element-119" data-mp="1jjq4eem97rax">Payment processors may collect and process financial information according to their own privacy policies and security practices.</p>
            <p data-id="element-120" data-mp="1elyfzuktinbh">Websites by Mandy does not intentionally store complete credit or debit card numbers when payment information is processed directly through a third-party payment processor.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-121" data-mp="38vigwlzfsahv">5. Cookies & Similar Technologies</h3>
            <p data-id="element-122" data-mp="3lg7f8id8qzul">Our website may use cookies or similar technologies to help the website function properly, remember preferences, understand website traffic, improve performance, or provide analytics.</p>
            <p data-id="element-123" data-mp="3v60npai36yum">Some cookies may be placed by third-party services used on our website.</p>
            <p data-id="element-124" data-mp="3v7uyvx3k3cli">You can usually control or disable cookies through your browser settings. Disabling certain cookies may affect how some portions of the website function.</p>
            <p data-id="element-125" data-mp="32hv0mh3k8xrm">Where required by applicable law, visitors will be provided with appropriate choices regarding non-essential cookies.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-126" data-mp="3cfj01kc42tg4">6. Analytics</h3>
            <p data-id="element-127" data-mp="mq79517bfxg7">Websites by Mandy may use website analytics services to better understand how visitors find and use our website.</p>
            <p data-id="element-128" data-mp="roijre5fqbzh">Analytics providers may collect information such as pages visited, general location, device information, browser information, and interactions with the website.</p>
            <p data-id="element-129" data-mp="3t7pkmtgcv50k">We use this information to evaluate website performance and improve our services.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-130" data-mp="bz2998u3glkj">7. Third-Party Services</h3>
            <p data-id="element-131" data-mp="6zpoquqp3ql4">Websites by Mandy may use third-party companies and technology providers to operate our business and website.</p>
            <p data-id="element-132" data-mp="3fh3x2rfyi6wc">These may include services for:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-133" data-mp="3agzi55up5k88">
              <li data-id="element-134" data-mp="3aiaj6xwrcdvn">Website hosting</li>
              <li data-id="element-135" data-mp="3af4wtqfijq3m">Domain and DNS services</li>
              <li data-id="element-136" data-mp="3p6xazz3kmy3r">Payment processing</li>
              <li data-id="element-137" data-mp="3p7yp6cfvw0pp">Email and communication</li>
              <li data-id="element-138" data-mp="3kevqxr05w336">Contact forms</li>
              <li data-id="element-139" data-mp="3kdkktkj8uj8w">Website analytics</li>
              <li data-id="element-140" data-mp="2wpkv8nxo4yvw">Cloud storage</li>
              <li data-id="element-141" data-mp="2wojcryebpa45">Security</li>
              <li data-id="element-142" data-mp="2wnq956anv0q8">Website development</li>
              <li data-id="element-143" data-mp="2rqzxqw9swtb5">Artificial intelligence and creative tools</li>
              <li data-id="element-144" data-mp="2rodq6wmwudsx">Other services necessary to operate our business</li>
            </ul>
            <p data-id="element-145" data-mp="1774l7hdq9qd6">These providers may process information when necessary to perform services on our behalf.</p>
            <p data-id="element-146" data-mp="1c18tq52n66gd">Third-party companies maintain their own privacy policies and practices, and Websites by Mandy is not responsible for the privacy practices of independent third-party websites or services.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-147" data-mp="127017hdm2di7">8. AI & Technology Tools</h3>
            <p data-id="element-148" data-mp="1px41berlbv4g">Websites by Mandy may use artificial intelligence and other technology tools to assist with website design, development, written content, graphics, imagery, video, animation, coding, and other aspects of our services.</p>
            <p data-id="element-149" data-mp="1kz2lr3tdidxq">When reasonably necessary to complete a client's project, information or materials supplied by the client may be processed using third-party technology or AI-assisted services.</p>
            <p data-id="element-150" data-mp="9kxfg45otrz8">Clients should avoid providing passwords, financial account information, Social Security numbers, medical records, or other highly sensitive personal information unless specifically requested and necessary for the service being provided.</p>
            <p data-id="element-151" data-mp="kz0jj2g6ras1">We make reasonable efforts to limit information shared with third-party tools to information appropriate for completing the requested service.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-152" data-mp="b48i1njhg3rz">9. Client Website Information</h3>
            <p data-id="element-153" data-mp="18q5vh6j09ix2">During a website project, clients may provide Websites by Mandy with business information, photographs, logos, written materials, product information, account information, or other content.</p>
            <p data-id="element-154" data-mp="yqorqunbdf3q">We use these materials for the purpose of completing the client's project and providing requested services.</p>
            <p data-id="element-155" data-mp="ut99bidu0pmi">Clients are responsible for ensuring they have permission to provide and use any personal information, photographs, copyrighted material, trademarks, or other content they submit to Websites by Mandy.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-156" data-mp="1uzmq1n8wleqi">10. How We Share Information</h3>
            <p data-id="element-157" data-mp="1yzwuuwkylz3s">Websites by Mandy does not sell personal information.</p>
            <p data-id="element-158" data-mp="23zrlcz0tomob">We may share limited information with service providers when reasonably necessary to:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-159" data-mp="28qhtgk0mv3mp">
              <li data-id="element-160" data-mp="28s2jpxfx00er">Operate our website</li>
              <li data-id="element-161" data-mp="2dpkwjyx1yzs1">Process payments</li>
              <li data-id="element-162" data-mp="2dojbd1mbowwl">Communicate with clients</li>
              <li data-id="element-163" data-mp="2drxa7zvs2nza">Provide website design and development services</li>
              <li data-id="element-164" data-mp="2in4asytr3cjz">Store project materials</li>
              <li data-id="element-165" data-mp="2mqir7qllpuxy">Maintain security</li>
              <li data-id="element-166" data-mp="2mrkdkr82i3uz">Provide other services necessary to operate our business</li>
            </ul>
            <p data-id="element-167" data-mp="2ro2gdlju7s4o">We may also disclose information if required by law, court order, legal process, or when reasonably necessary to protect our rights, customers, business, or others.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-168" data-mp="xajddw71fhbz">11. Data Security</h3>
            <p data-id="element-169" data-mp="20lgpfxkodn8s">Websites by Mandy takes reasonable administrative and technical measures to protect information under our control.</p>
            <p data-id="element-170" data-mp="1voqc3i0995r2">However, no website, internet transmission, electronic storage system, or online service can guarantee absolute security.</p>
            <p data-id="element-171" data-mp="24ntwcpb5rr75">Clients and visitors should use caution when transmitting sensitive information online and should not send passwords, Social Security numbers, banking credentials, or similarly sensitive information through ordinary contact forms or email unless specifically necessary.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-172" data-mp="2ek6txy9tqp76">12. Data Retention</h3>
            <p data-id="element-173" data-mp="2sf92yu9ywuo7">We may retain personal and business information for as long as reasonably necessary to:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-174" data-mp="32bltxke4ouf9">
              <li data-id="element-175" data-mp="32c3w0xnh22v3">Provide requested services</li>
              <li data-id="element-176" data-mp="2xcracg8c71u3">Complete website projects</li>
              <li data-id="element-177" data-mp="2xe2ewi9lwdm9">Maintain project and transaction records</li>
              <li data-id="element-178" data-mp="3gxrkgv0pjtjr">Respond to future questions regarding completed projects</li>
              <li data-id="element-179" data-mp="3lxdtstdf0pna">Meet accounting, tax, or legal obligations</li>
              <li data-id="element-180" data-mp="3lwmbulcqqqfh">Resolve disputes</li>
              <li data-id="element-181" data-mp="370de5o8i0l56">Protect our legitimate business interests</li>
            </ul>
            <p data-id="element-182" data-mp="372hfbyf0j61r">Information that is no longer reasonably necessary may be deleted or securely disposed of when appropriate.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-183" data-mp="2y79sisnp91zg">13. Your Privacy Choices</h3>
            <p data-id="element-184" data-mp="32z1nrghsvvvx">Depending on where you live and applicable privacy laws, you may have certain rights regarding your personal information.</p>
            <p data-id="element-185" data-mp="2ob6t3uk8fhg7">You may contact Websites by Mandy to request that we:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-186" data-mp="2t9rtbtzhi14n">
              <li data-id="element-187" data-mp="2t6dngbrm39sg">Provide information about personal information we maintain about you</li>
              <li data-id="element-188" data-mp="2fb1t45hdblc2">Correct inaccurate information</li>
              <li data-id="element-189" data-mp="2j9hds2pz8hmb">Delete certain personal information when legally permitted</li>
              <li data-id="element-190" data-mp="2zql617318y6z">Address a privacy-related question or concern</li>
            </ul>
            <p data-id="element-191" data-mp="2uqyrmubgxbwl">Privacy requests may be submitted to: websitesbymandy@gmail.com</p>
            <p data-id="element-192" data-mp="2up48nljrbtv5">Some information may need to be retained for legal, tax, accounting, fraud-prevention, contractual, or legitimate business purposes.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-193" data-mp="2krr79n6bggzz">14. Children's Privacy</h3>
            <p data-id="element-194" data-mp="2gs8trcgtr876">Websites by Mandy and its services are intended for adults and businesses seeking website design or related services.</p>
            <p data-id="element-195" data-mp="2bye4zumd4318">We do not knowingly collect personal information directly from children under the age of 13.</p>
            <p data-id="element-196" data-mp="26z1lnjup9e3e">If we become aware that personal information from a child under 13 has been collected without appropriate authorization, we will take reasonable steps to delete the information.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-197" data-mp="1x514ukq22keu">15. Links to Other Websites</h3>
            <p data-id="element-198" data-mp="8jkixugnplbf">Our website may contain links to third-party websites or services.</p>
            <p data-id="element-199" data-mp="8lwyy22utllu">Once you leave Websites by Mandy, the privacy practices of the third-party website or service apply.</p>
            <p data-id="element-200" data-mp="hkidlm57gghc">Websites by Mandy is not responsible for the content, security, privacy policies, or privacy practices of websites or services operated by other companies.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-201" data-mp="mh92myyzmmew">16. Changes to This Privacy Policy</h3>
            <p data-id="element-202" data-mp="3q023qcakxis6">Websites by Mandy may update this Privacy Policy as our business, website, technology, services, or legal requirements change.</p>
            <p data-id="element-203" data-mp="3uy3vdlm2mqf9">When changes are made, the updated version will be posted on this page with a revised “Last Updated” date.</p>
            <p data-id="element-204" data-mp="3lsiud5uysw5">We encourage visitors to review this Privacy Policy periodically.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-205" data-mp="329ycz5ihpdil">17. Contact Us</h3>
            <p data-id="element-206" data-mp="3789kh9lbbbxn">If you have questions about this Privacy Policy, have concerns about how your information is handled, or would like to make a privacy-related request, please contact:</p>
            
            <div className="pt-4 pb-4" data-id="element-207" data-mp="2lxfbfl2srgga">
              <p className="font-[DM_Serif_Display] text-xl text-white" data-id="element-208" data-mp="2lwdu4yv6txq4">Websites by Mandy</p>
              <p className="mt-2 text-sm text-white/60" data-id="element-209" data-mp="281bhztzqzc4v">Email: websitesbymandy@gmail.com</p>
              <p className="text-sm text-white/60" data-id="element-210" data-mp="2c2n9y663g9g8">Website: websitesbymandy.com</p>
              <p className="mt-4 text-xs text-white/40" data-id="element-211" data-mp="1y7kuosfcx7qr">Last Updated: August 15, 2026</p>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}

// --- Pricing Dialog ---
function PricingDialog({
  onClose
}: {
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} className="fixed inset-0 z-[400] flex flex-col items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="pricing-title" data-id="element-212" data-mp="1tb49a5zjl7hd">
      {/* Close Button */}
      <button onClick={onClose} className="absolute right-6 top-6 z-50 inline-flex size-10 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white/90 shadow-lg backdrop-blur-md transition-colors hover:bg-black/60 hover:text-white" aria-label="Close pricing dialog" data-id="element-213" data-mp="vohxi4e00t5x">
        <XIcon size={20} data-id="element-214" data-mp="1jlf6hog92kpf" />
      </button>

      {/* Transparent Content Container */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 20
    }} transition={{
      duration: 0.5,
      delay: 0.2
    }} className="relative z-10 flex h-full max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden" data-id="element-215" data-mp="1envukv70yk0n">
        <div className="flex-1 overflow-y-auto p-6 text-sm leading-relaxed text-white drop-shadow-md sm:p-10 sm:text-base [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] [text-shadow:0_2px_5px_rgba(0,0,0,1),0_0_15px_rgba(0,0,0,0.8)]" data-id="element-216" data-mp="22ceykyj1kamx">
          <div className="mx-auto max-w-2xl space-y-12 pb-20 font-medium tracking-wide text-center" data-id="element-217" data-mp="uymfuyt1v4f3">
            
            <section data-id="element-218" data-mp="141dut8l4wibh">
              <h3 id="pricing-title" className="mb-4 font-[DM_Serif_Display] text-5xl text-amber-400 drop-shadow-lg sm:text-6xl" data-id="element-219" data-mp="140vmhdl76dn7">Website Design</h3>
              <p className="mx-auto mb-10 max-w-xl text-lg sm:text-xl sm:leading-relaxed" data-id="element-220" data-mp="18tyma3gkijbu">
                Choose the starting point that best fits your business. Every project is tailored to your goals, and additional work is billed at $50 per hour.
              </p>

              <div className="grid grid-cols-1 gap-6 text-left md:grid-cols-3" data-id="element-221" data-mp="1nqfuwmxdhjup">
                <article className="flex flex-col rounded-2xl border border-white/10 bg-black/60 p-6 shadow-lg backdrop-blur-md" data-id="element-222" data-mp="1rqzoxhmng7cm">
                  <h4 className="font-[DM_Serif_Display] text-2xl text-white" data-id="element-223" data-mp="3piarrweyzjsv">Basic Website Design</h4>
                  <p className="my-4 text-3xl font-bold text-amber-300" data-id="element-224" data-mp="3tictu6omaf8o">$300<span className="text-base font-medium text-white/60" data-id="element-225" data-mp="3lyttamjby3cc"> starting price</span></p>
                  <p className="flex-1 text-sm leading-relaxed text-white/85" data-id="element-226" data-mp="3h7ljvqi02hz6">Covers the first hour. Designed for simpler sites, with more use of stock imagery and content.</p>
                  <p className="mt-6 text-xs font-medium text-white/60 italic" data-id="element-227" data-mp="3875g0fxch8ts">Additional work is $50/hour.</p>
                </article>

                <article className="relative flex flex-col overflow-hidden rounded-2xl border border-amber-500/50 bg-black/80 p-6 shadow-lg backdrop-blur-md" data-id="element-228" data-mp="8qz5z1alntcn">
                  <div className="absolute right-0 top-0 rounded-bl-lg bg-amber-500/30 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-200" data-id="element-229" data-mp="3vwq237osom7a">Most Popular</div>
                  <h4 className="font-[DM_Serif_Display] text-2xl text-white" data-id="element-230" data-mp="3qzr9wuooypbq">Enhanced Website Design</h4>
                  <p className="my-4 text-3xl font-bold text-amber-300" data-id="element-231" data-mp="2kf88h10xh2yh">$500<span className="text-base font-medium text-white/60" data-id="element-232" data-mp="2fhotu00bio60"> starting price</span></p>
                  <p className="flex-1 text-sm leading-relaxed text-white/85" data-id="element-233" data-mp="7907ecu2pwo3">Covers the first two hours. Includes more customized design, visuals, animations, details, and overall personalization.</p>
                  <p className="mt-6 text-xs font-medium text-white/60 italic" data-id="element-234" data-mp="3ufsszsq17ejz">Additional work is $50/hour.</p>
                </article>

                <article className="flex flex-col rounded-2xl border border-white/10 bg-black/60 p-6 shadow-lg backdrop-blur-md" data-id="element-235" data-mp="28cibwpenpwi">
                  <h4 className="font-[DM_Serif_Display] text-2xl text-white" data-id="element-236" data-mp="uzw85zgic27h">E-Commerce Website Design</h4>
                  <p className="my-4 text-3xl font-bold text-amber-300" data-id="element-237" data-mp="h0ed52cmbspo">$600<span className="text-base font-medium text-white/60" data-id="element-238" data-mp="h1pkfc59vilj"> starting price</span></p>
                  <p className="flex-1 text-sm leading-relaxed text-white/85" data-id="element-239" data-mp="m0skei6eaymn">Built for online stores where products and a storefront need to be added and configured. Clients need their own third-party payment processor or account, such as Square.</p>
                  <p className="mt-6 text-xs font-medium text-white/60 italic" data-id="element-240" data-mp="2y6v75zlmp7e3">Additional work is $50/hour.</p>
                </article>
              </div>
            </section>

            <div className="my-12 h-px w-full bg-white/10" data-id="element-241" data-mp="385u96zbw24s4" />

            <section data-id="element-242" data-mp="383zvo62y5ey2">
              <h3 className="mb-8 font-[DM_Serif_Display] text-4xl text-amber-400 drop-shadow-lg sm:text-5xl" data-id="element-243" data-mp="336zofrud265d">Monthly Maintenance Plans</h3>
              <p className="mb-10 text-lg sm:text-xl sm:leading-relaxed" data-id="element-244" data-mp="2figgjdf83vdt">
                After your website is completed, keep it secure, updated, and running smoothly with an optional maintenance plan.
              </p>

              <div className="grid grid-cols-1 gap-6 text-left md:grid-cols-3" data-id="element-245" data-mp="2p8j7l82saz9i">
                {/* Monitoring Plan */}
                <div className="flex flex-col rounded-2xl border border-white/10 bg-black/60 p-6 shadow-lg backdrop-blur-md" data-id="element-246" data-mp="2keorsfqav4fp">
                  <h4 className="font-[DM_Serif_Display] text-2xl text-white" data-id="element-247" data-mp="1rsce39dru1oe">Monitoring Plan</h4>
                  <div className="my-4 text-3xl font-bold text-amber-300" data-id="element-248" data-mp="3ruc01ez8274a">$25<span className="text-lg text-white/50" data-id="element-249" data-mp="3rttv7tyhukkr">/mo</span></div>
                  <ul className="mb-6 flex-1 space-y-3 text-sm text-white/80" data-id="element-250" data-mp="mhlfzzxx9ev">
                    <li className="flex items-start gap-2" data-id="element-251" data-mp="5e93tn8y08tx"><span className="text-amber-500" data-id="element-252" data-mp="5fapelnl4dpw">•</span> Website checked twice per month</li>
                    <li className="flex items-start gap-2" data-id="element-253" data-mp="9fb3242v0kvw"><span className="text-amber-500" data-id="element-254" data-mp="38vhhduwn0miu">•</span> Contact forms tested twice per month</li>
                    <li className="flex items-start gap-2" data-id="element-255" data-mp="3du143xj222c9"><span className="text-amber-500" data-id="element-256" data-mp="3dv3wk9w10cua">•</span> Basic uptime monitoring</li>
                    <li className="flex items-start gap-2" data-id="element-257" data-mp="3huus0qwhzh55"><span className="text-amber-500" data-id="element-258" data-mp="3hy0kmg4c515m">•</span> Broken links checks</li>
                    <li className="flex items-start gap-2" data-id="element-259" data-mp="3mwafepm5dzqp"><span className="text-amber-500" data-id="element-260" data-mp="x5xc9yolob7h">•</span> Notification if issues are found</li>
                  </ul>
                  <p className="text-xs font-medium text-white/60 italic" data-id="element-261" data-mp="123yvcjm10aiy">
                    No content edits included. Any requested changes billed separately.
                  </p>
                </div>

                {/* Maintenance Plan */}
                <div className="flex flex-col rounded-2xl border border-amber-500/50 bg-black/80 p-6 shadow-lg backdrop-blur-md relative overflow-hidden" data-id="element-262" data-mp="17bouqojsgu0t">
                  <div className="absolute top-0 right-0 bg-amber-500/30 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-300 rounded-bl-lg" data-id="element-263" data-mp="yi3cgv8f0umn">Popular</div>
                  <h4 className="font-[DM_Serif_Display] text-2xl text-white" data-id="element-264" data-mp="ojdwj802xk91">Maintenance Plan</h4>
                  <div className="my-4 text-3xl font-bold text-amber-300" data-id="element-265" data-mp="jjrhqu4lrvy4">$49<span className="text-lg text-white/50" data-id="element-266" data-mp="jkb20hhq6wfb">/mo</span></div>
                  <ul className="mb-6 flex-1 space-y-3 text-sm text-white/80" data-id="element-267" data-mp="fjhcxksjrbku">
                    <li className="flex items-start gap-2" data-id="element-268" data-mp="2dykbsopu8eny"><span className="text-amber-500" data-id="element-269" data-mp="2dxiqzsj0sdt1">•</span> Everything in the Monitoring Plan</li>
                    <li className="flex items-start gap-2" data-id="element-270" data-mp="29wyrwjevz049"><span className="text-amber-500" data-id="element-271" data-mp="2t5703tkji30g">•</span> Minor text updates & photo replacements</li>
                    <li className="flex items-start gap-2" data-id="element-272" data-mp="2x6kamgykuudp"><span className="text-amber-500" data-id="element-273" data-mp="2x5ilx9on29di">•</span> Contact information changes</li>
                    <li className="flex items-start gap-2" data-id="element-274" data-mp="2jeci51ebtpkt"><span className="text-amber-500" data-id="element-275" data-mp="2jdb2skqa2gxa">•</span> Up to 30 minutes of edits per month</li>
                  </ul>
                  <p className="text-xs font-medium text-amber-200/80 italic" data-id="element-276" data-mp="29hhhocyhtf3w">
                    (This is only necessary if you think you are going to need changes throughout the month, like an "Employee of the Month" update, etc.)
                  </p>
                </div>

                {/* Care Plan */}
                <div className="flex flex-col rounded-2xl border border-white/10 bg-black/60 p-6 shadow-lg backdrop-blur-md" data-id="element-277" data-mp="25f4enwb4rvat">
                  <h4 className="font-[DM_Serif_Display] text-2xl text-white" data-id="element-278" data-mp="3uvvq172uqgpp">Care Plan</h4>
                  <div className="my-4 text-3xl font-bold text-amber-300" data-id="element-279" data-mp="3n84aq0p8rhm">$99<span className="text-lg text-white/50" data-id="element-280" data-mp="ds7q01qk16j3">/mo</span></div>
                  <ul className="mb-6 flex-1 space-y-3 text-sm text-white/80" data-id="element-281" data-mp="dsg5m1e2apxz">
                    <li className="flex items-start gap-2" data-id="element-282" data-mp="8uobd0py2rqm"><span className="text-amber-500" data-id="element-283" data-mp="8raajwwxiqla">•</span> Everything in the Maintenance Plan</li>
                    <li className="flex items-start gap-2" data-id="element-284" data-mp="htt9i5xlgj29"><span className="text-amber-500" data-id="element-285" data-mp="hsqje083ikne">•</span> Priority support</li>
                    <li className="flex items-start gap-2" data-id="element-286" data-mp="wi66954ia5xt"><span className="text-amber-500" data-id="element-287" data-mp="wh4ic02e5oyo">•</span> Up to 1 hour of edits per month</li>
                    <li className="flex items-start gap-2" data-id="element-288" data-mp="rj30figpbvos"><span className="text-amber-500" data-id="element-289" data-mp="15jdsupxntic2">•</span> New sections, pages, and small additions</li>
                    <li className="flex items-start gap-2" data-id="element-290" data-mp="11hsibb8llg51"><span className="text-amber-500" data-id="element-291" data-mp="11ittrgtj04na">•</span> Ongoing website optimization</li>
                  </ul>
                  <p className="text-xs font-medium text-white/60 italic" data-id="element-292" data-mp="37b8zgr526wnf">
                    (This plan is only necessary if you're doing consistent changes throughout the month. If you're a business that's constantly moving and changing, this is for you.)
                  </p>
                </div>
              </div>

              <div className="mt-10 rounded-xl bg-black/40 p-5 text-sm text-white/70 border border-white/5" data-id="element-293" data-mp="1dl7peotjevur">
                <strong className="text-white" data-id="element-294" data-mp="1nhaspy04l0ja">Need extra help?</strong> Any additional edits on top of your plan are $50.00 an hour with a 30-minute minimum. Most changes can be done in less than 30 minutes!
              </div>
            </section>

          </div>
        </div>
      </motion.div>
    </motion.div>;
}

// --- About Dialog ---
function AboutDialog({
  onClose
}: {
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} className="fixed inset-0 z-[400] flex flex-col items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="about-title" data-id="element-295" data-mp="2680ypvkzt3gd">
      {/* Close Button */}
      <button onClick={onClose} className="absolute top-6 right-6 z-50 inline-flex size-10 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white/90 shadow-lg backdrop-blur-md transition-colors hover:bg-black/60 hover:text-white" aria-label="Close about dialog" data-id="element-296" data-mp="2yv1xwjyg1awm">
        <XIcon size={20} data-id="element-297" data-mp="2g3rcgibhlqc4" />
      </button>

      {/* Transparent Content Container */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 20
    }} transition={{
      duration: 0.5,
      delay: 0.2
    }} className="relative z-10 flex h-full max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden" data-id="element-298" data-mp="2g1y3gwdeiu0y">
        <div className="flex-1 overflow-y-auto p-6 text-sm leading-relaxed text-white sm:p-10 sm:text-base [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] [text-shadow:0_2px_5px_rgba(0,0,0,1),0_0_15px_rgba(0,0,0,0.8)]" data-id="element-299" data-mp="2b2boahe0j3pi">
          <div className="mx-auto max-w-2xl space-y-16 pb-20 font-medium tracking-wide" data-id="element-300" data-mp="7ot6ei5nzvrc">
            
            <section data-id="element-301" data-mp="2w9j2th6dm9c">
              <h3 className="mb-6 font-[DM_Serif_Display] text-4xl text-amber-400 drop-shadow-lg sm:text-5xl" data-id="element-302" data-mp="2vqe0dcmiscq">Our Mission</h3>
              <p className="mb-5 text-lg font-medium sm:text-xl sm:leading-relaxed" data-id="element-303" data-mp="3q1r38g75u8ha">
                At Websites by Mandy, our mission is to help small businesses build a strong foundation from the very beginning by providing accessible, professional websites that give them the visibility they need to get their business off the ground.
              </p>
              <p className="text-lg font-medium sm:text-xl sm:leading-relaxed" data-id="element-304" data-mp="hkmlo9arq3ct">
                We believe every great business deserves a place to be seen, because before your business can grow, people have to know you exist.
              </p>
            </section>

            <section data-id="element-305" data-mp="2xck4ph7phiw1">
              <h3 className="mb-6 font-[DM_Serif_Display] text-4xl text-amber-400 drop-shadow-lg sm:text-5xl" data-id="element-306" data-mp="2xcad5p70j3o8">About Me</h3>
              <p className="mb-5 text-2xl font-bold text-white drop-shadow-lg" data-id="element-307" data-mp="1cwvrjjtge0ag">Hi, I'm Mandy.</p>
              <p className="mb-5 text-base sm:text-lg sm:leading-relaxed" data-id="element-308" data-mp="1hudturcyth9h">
                I’m an entrepreneur, creator, and the woman behind Websites by Mandy. Like a lot of small-business owners, I didn’t start out knowing exactly what I was doing—I learned by building, experimenting, making mistakes, starting over, and figuring things out along the way. When I started my own business, I eventually realized that one of the very first things I should have created was a professional place online where people could actually find me, understand what I offered, and connect with my business.
              </p>
              <p className="mb-5 text-base sm:text-lg sm:leading-relaxed" data-id="element-309" data-mp="29j7fj4m4zmqg">
                That experience is a huge part of why Websites by Mandy exists.
              </p>
              <p className="mb-5 text-base sm:text-lg sm:leading-relaxed" data-id="element-310" data-mp="f7sqyr4v3tca">
                I know what it feels like to have big ideas without a big-business budget or an entire team behind you. I also know how overwhelming technology can feel when you're already trying to handle everything else that comes with building a business. I've spent years creating online, building brands, learning new technology, and finding creative ways to turn ideas into something real—and now I get to use everything I've learned to help other entrepreneurs do the same.
              </p>
              <p className="mb-8 text-base sm:text-lg sm:leading-relaxed" data-id="element-311" data-mp="2sdp4tukw7xid">
                I don't believe you should have to be a huge company to have a beautiful, professional online presence. You can start small and still show up like you mean business.
              </p>
              <p className="text-xl font-bold text-amber-300 drop-shadow-lg sm:text-2xl sm:leading-relaxed" data-id="element-312" data-mp="2el96h72xsnns">
                That's what Websites by Mandy is all about: helping small businesses take that first important step toward being seen.
              </p>
            </section>

          </div>
        </div>
      </motion.div>
    </motion.div>;
}

// --- Terms & Conditions Dialog ---
function TermsDialog({
  onClose
}: {
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-[400] flex items-center justify-center bg-black/80 p-4 sm:p-6 backdrop-blur-md" role="dialog" aria-modal="true" aria-labelledby="terms-title" data-id="element-313" data-mp="g5judzrzc7pd">
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} transition={{
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative flex h-full max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-zinc-900 shadow-2xl" data-id="element-314" data-mp="yuq8f9htwb4u">
        <div className="flex items-center justify-between border-b border-white/10 bg-zinc-900/80 px-6 py-4 backdrop-blur-sm" data-id="element-315" data-mp="1u3jyfez0mv00">
          <h2 id="terms-title" className="font-[DM_Serif_Display] text-2xl text-white" data-id="element-316" data-mp="1p4qx0kx06ug7">Terms & Conditions</h2>
          <button onClick={onClose} className="inline-flex size-8 items-center justify-center rounded-full bg-white/10 text-white/70 transition-colors hover:bg-white/20 hover:text-white" aria-label="Close terms dialog" data-id="element-317" data-mp="1kbo1464r78wu">
            <XIcon size={18} data-id="element-318" data-mp="16coa4rtrqhxi" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 text-sm leading-relaxed text-white/80 sm:p-8 sm:text-base" data-id="element-319" data-mp="11eczievng3e8">
          <div className="mx-auto max-w-3xl space-y-6" data-id="element-320" data-mp="xdjhg24w1it9">
            <p className="font-bold text-white" data-id="element-321" data-mp="xf4blqnaizql">Websites by Mandy — Terms & Conditions</p>
            <p className="text-xs text-white/50" data-id="element-322" data-mp="2vrups9nfvecy">Last Updated: August 15, 2026</p>
            <p data-id="element-323" data-mp="2rrkc0atenjnj">By purchasing website design or related services from Websites by Mandy (“Websites by Mandy,” “we,” “us,” or “our”), you agree to the following Terms & Conditions.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-324" data-mp="ihclem5sijq4">1. Website Mock-Up & Approval</h3>
            <p data-id="element-325" data-mp="nae1uylnarbt">Before paid development begins, Websites by Mandy may provide the client with an initial website mock-up or concept.</p>
            <p data-id="element-326" data-mp="rciupv49rjxe">The mock-up is intended to establish the overall design direction, style, layout, and appearance of the website.</p>
            <p data-id="element-327" data-mp="3voeci0d6tmlx">Once the client approves the mock-up, the project moves into development and the payment terms below apply.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-328" data-mp="3kc3tbhbc55d">2. Payment</h3>
            <p data-id="element-329" data-mp="8l9i3cz7cf1l">After the client approves the mock-up:</p>
            <ul className="list-disc space-y-2 pl-5" data-id="element-330" data-mp="1f0aipvz69lei">
              <li data-id="element-331" data-mp="1ezhaeqkkenu9">50% of the agreed project price is due before development begins.</li>
              <li data-id="element-332" data-mp="1jyc26i0ifmqi">The remaining 50% is due when the website is completed and before final delivery, transfer, publication, or release of the completed project, unless otherwise agreed to in writing.</li>
            </ul>
            <p data-id="element-333" data-mp="qp9vut8dldaf">Work will not begin until the initial 50% payment has been received.</p>
            <p data-id="element-334" data-mp="14fwsmfukst3v">Websites by Mandy may withhold final files, website access, transfers, deployment, or other project deliverables until the outstanding balance has been paid in full.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-335" data-mp="1ec87ndljvx20">3. Refunds & Cancellations</h3>
            <p data-id="element-336" data-mp="19d54xzt1m2x7">Because website design involves customized work performed specifically for each client, payments for work that has already been performed are non-refundable.</p>
            <p data-id="element-337" data-mp="1jbunlusrjrei">Once the client approves the mock-up and Websites by Mandy begins development, the initial payment compensates Websites by Mandy for time, design, development, creative work, and other services performed on the project.</p>
            <p data-id="element-338" data-mp="3l5bs4pllkc51">Once the website has been completed, no refunds will be provided for completed work.</p>
            <p data-id="element-339" data-mp="3b0c251oplaxr">Approval of a mock-up does not mean that every minor detail of the completed website will be identical to the mock-up. Reasonable adjustments may occur during development due to functionality, device responsiveness, platform limitations, or technical requirements.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-340" data-mp="367sq495o6nv2">4. Client Changes & Revisions</h3>
            <p data-id="element-341" data-mp="2ra9uqnyhlcen">The client is responsible for reviewing the website and communicating requested changes during the development process.</p>
            <p data-id="element-342" data-mp="2ib4wliqu7l30">Reasonable revisions that remain within the originally agreed project scope may be included.</p>
            <p data-id="element-343" data-mp="2n9y7r4inj000">Requests that substantially change the approved design, add new pages or functionality, or otherwise expand the original project may require an additional fee.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-344" data-mp="ghxk0h2lzhkt">5. AI-Assisted Design & Development</h3>
            <p data-id="element-345" data-mp="1nar9d1vk0jx4">Websites by Mandy uses modern digital and artificial intelligence tools as part of its creative and development process.</p>
            <p data-id="element-346" data-mp="1sadnddj7i46z">These tools may include AI-assisted website design and development platforms, such as Magic Patterns, as well as artificial intelligence tools used to assist with graphics, imagery, video, animation, written content, layout concepts, coding, and other creative or technical elements.</p>
            <p data-id="element-347" data-mp="2607czy2jcshr">By hiring Websites by Mandy, the client acknowledges and agrees that AI-assisted tools may be used in the creation of their website and related materials.</p>
            <p data-id="element-348" data-mp="2fyww1u664izl">AI-assisted content may occasionally contain inaccuracies, inconsistencies, or other limitations. Websites by Mandy reviews and modifies generated materials as appropriate but does not guarantee that every element of a project was created exclusively by a human or entirely from scratch.</p>
            <p data-id="element-349" data-mp="qjgq0nnjjddx">Unless specifically agreed to in writing, the client is purchasing the completed website design and services—not exclusive ownership of the underlying third-party software, AI models, platforms, stock assets, fonts, templates, or other third-party materials used to create it.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-350" data-mp="2v1eul7d5vxec">6. Domains</h3>
            <p data-id="element-351" data-mp="2q3mzy5l4tx6g">Domain registration is not included unless specifically stated otherwise.</p>
            <p data-id="element-352" data-mp="3mt47yr7l7vbb">Clients who want a custom domain are responsible for purchasing and maintaining their own domain name.</p>
            <p data-id="element-353" data-mp="3isu01908iutt">Websites by Mandy may recommend third-party domain registrars, including Cloudflare, based on factors such as affordability and available services. However, the client is responsible for selecting their registrar, purchasing the domain, maintaining the account, paying renewal fees, and complying with that provider's terms.</p>
            <p data-id="element-354" data-mp="22bg02wg7782a">Any third-party provider recommended by Websites by Mandy remains an independent company. Websites by Mandy is not responsible for changes in pricing, policies, service availability, outages, account issues, or other actions taken by third-party providers.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-355" data-mp="23u683wmmcfgp">7. DNS & Domain Connection</h3>
            <p data-id="element-356" data-mp="1punc22730ukr">Websites by Mandy will provide the client with the DNS records or other information needed to connect the client's domain to their website.</p>
            <p data-id="element-357" data-mp="1g4lqpi645q9j">The client is responsible for entering those records into their domain registrar or DNS provider unless a separate service has been agreed upon.</p>
            <p data-id="element-358" data-mp="1kxoknresz1q9">Websites by Mandy will provide the necessary information and reasonable instructions but cannot be responsible for errors caused by incorrectly entered DNS records, registrar restrictions, DNS propagation delays, expired domains, account access problems, or issues originating with the client's domain provider.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-359" data-mp="125vo5n0iyyds">8. Client Responsibilities</h3>
            <p data-id="element-360" data-mp="iir5ue382li1">The client is responsible for providing accurate information and any necessary content, images, logos, business information, account access, approvals, and other materials reasonably required to complete the project.</p>
            <p data-id="element-361" data-mp="sjb7hhunls0s">The client is also responsible for reviewing the website before final approval and ensuring that business information, pricing, services, contact information, legal disclosures, and other client-provided information are accurate.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-362" data-mp="wjlij8xaz8mi">9. Third-Party Services</h3>
            <p data-id="element-363" data-mp="1b8rb4e1my0lf">Websites may rely on third-party platforms and services for functions such as hosting, domains, forms, analytics, payments, email, integrations, APIs, AI functionality, or other features.</p>
            <p data-id="element-364" data-mp="1k84ohhsvr9bf">Websites by Mandy cannot guarantee the continued availability or performance of third-party services and is not responsible for outages, pricing changes, policy changes, discontinued features, or other actions outside of our control.</p>
            <p data-id="element-365" data-mp="39nxgl0se7el4">Websites by Mandy is not responsible for the loss, interruption, or unavailability of website functionality, hosting, images, videos, files, integrations, or other content resulting from the shutdown, discontinuation, outage, policy change, or failure of a third-party platform or service, including Magic Patterns.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-366" data-mp="2qrp9drftktn6">10. Website Performance & Compatibility</h3>
            <p data-id="element-367" data-mp="2vrbm7zrqk7vg">Websites by Mandy will make reasonable efforts to create a functional and responsive website.</p>
            <p data-id="element-368" data-mp="30ok8abg08w1z">However, websites may display or function differently depending on the visitor's device, browser, operating system, internet connection, screen size, third-party services, or future software updates.</p>
            <p data-id="element-369" data-mp="fyutfyfaxa9a">Perfect compatibility with every device, browser, or future technology cannot be guaranteed.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-370" data-mp="k2ss680psj1t">11. No Guarantee of Business Results</h3>
            <p data-id="element-371" data-mp="20h59vj9ewm7a">Websites by Mandy does not guarantee that a website will produce a specific number of visitors, customers, sales, search-engine rankings, leads, revenue, or other business results.</p>
            <p data-id="element-372" data-mp="1qrdckh9xhez4">Website performance depends on many factors outside of the design itself, including marketing, competition, pricing, SEO, advertising, customer demand, and the client's business practices.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-373" data-mp="1gtfrg2nd7yaq">12. Client Content & Intellectual Property</h3>
            <p data-id="element-374" data-mp="1cqsx9v3821ad">The client represents that they have permission to use any logos, photographs, videos, written content, trademarks, or other materials they provide to Websites by Mandy.</p>
            <p data-id="element-375" data-mp="37657b0ujqybl">The client is responsible for claims arising from materials supplied by the client without the necessary permission or licensing.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-376" data-mp="327act1n7vlbn">13. Portfolio Rights</h3>
            <p data-id="element-377" data-mp="3l9ucmmw19r55">Unless otherwise agreed to in writing, Websites by Mandy may display completed website designs, screenshots, or portions of completed projects in its portfolio, website, social media, or marketing materials for the purpose of demonstrating previous work.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-378" data-mp="3habutzzidw6j">14. Limitation of Liability</h3>
            <p data-id="element-379" data-mp="8u8njvcb69iq">To the fullest extent permitted by applicable law, Websites by Mandy will not be liable for indirect, incidental, consequential, or special damages arising from the client's website or third-party services.</p>
            <p data-id="element-380" data-mp="3v31l4gm2ln9g">Websites by Mandy's total liability relating to a project will not exceed the amount the client paid Websites by Mandy for the specific project giving rise to the claim, to the extent permitted by law.</p>

            <h3 className="pt-4 font-[DM_Serif_Display] text-xl text-white" data-id="element-381" data-mp="2oklerlebgk7s">15. Acceptance of These Terms</h3>
            <p data-id="element-382" data-mp="2h0ubv3aj8lei">Payment of the initial project payment after approval of the mock-up constitutes acknowledgment and acceptance of these Terms & Conditions unless a separate written agreement states otherwise.</p>
            <p data-id="element-383" data-mp="2q2aayevwd13f">These Terms & Conditions may be updated periodically. The terms applicable to a client project will be those provided or made available when the client enters into the agreement.</p>
            
            <div className="pt-8 pb-4" data-id="element-384" data-mp="1xeeftelz7h8r">
              <p className="font-[DM_Serif_Display] text-xl text-white" data-id="element-385" data-mp="1xba7q65e41ct">Websites by Mandy</p>
              <p className="mt-2 text-sm text-white/60" data-id="element-386" data-mp="1t9okprz566ow">For questions regarding these Terms & Conditions, please contact Websites by Mandy using the contact information provided on our website.</p>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}

// --- Contact Dialog ---
function ContactDialog({
  onClose
}: {
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5
  }} className="fixed inset-0 z-[400] flex flex-col items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="contact-title" data-id="element-387" data-mp="19n9152rruv01">
      {/* Close Button */}
      <button onClick={onClose} className="absolute top-6 right-6 z-50 inline-flex size-10 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white/90 shadow-lg backdrop-blur-md transition-colors hover:bg-black/60 hover:text-white" aria-label="Close contact dialog" data-id="element-388" data-mp="1329g388u7dz9">
        <XIcon size={20} data-id="element-389" data-mp="1gufwmvfwjmz8" />
      </button>

      {/* Content Container */}
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} transition={{
      duration: 0.4,
      delay: 0.1
    }} className="relative z-10 w-full max-w-3xl overflow-hidden rounded-2xl border border-white/20 bg-white/10 shadow-2xl backdrop-blur-md" data-id="element-390" data-mp="1lshdratrpc82">
        <div className="h-[85vh] max-h-[800px] w-full" data-id="element-391" data-mp="3b9moatrapz5h">
          <iframe id="JotFormIFrame-262267272074054" title="Contact Form" allowTransparency={true} allowFullScreen={true} allow="geolocation; microphone; camera" src="https://form.jotform.com/262267272074054" frameBorder="0" style={{
          minWidth: '100%',
          height: '100%',
          border: 'none'
        }} scrolling="yes" data-id="element-392" data-mp="3b81sl0t8wa61" />
        </div>
      </motion.div>
    </motion.div>;
}

// --- Main App ---
export function App() {
  const [isVideoEnded, setIsVideoEnded] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [tourProject, setTourProject] = useState<Project | null>(null);
  const [liveProject, setLiveProject] = useState<Project | null>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  useEffect(() => {
    document.title = "Websites by Mandy";
    if (videoRef.current) {
      videoRef.current.playbackRate = 1.25; // Speeds up the video by 25%
    }
  }, []);
  const isScreenActive = showMenu || tourProject !== null || liveProject !== null;
  const showSharedVideo = showAbout || showContact || showPricing;
  const goHome = () => {
    setShowMenu(false);
    setShowContact(false);
    setShowTerms(false);
    setShowPrivacy(false);
    setShowAbout(false);
    setShowPricing(false);
    setIsMobileNavOpen(false);
    setTourProject(null);
    setLiveProject(null);
  };
  return <main className="relative min-h-screen w-full overflow-hidden bg-black font-[DM_Sans] text-white" data-id="element-393" data-mp="3515cg1xjpp3v">
      
      {/* 1. The Garden Park Background Video */}
      <motion.video ref={videoRef} src="https://cdn.magicpatterns.com/uploads/85WBn9WKdHshxpC3fuNWJB/intro.mp4" autoPlay muted playsInline onEnded={() => setIsVideoEnded(true)} animate={{
      scale: isVideoEnded ? 1.4 : 1,
      filter: isVideoEnded ? "blur(12px)" : "blur(0px)",
      opacity: isVideoEnded ? 0 : 1
    }} transition={{
      duration: 2,
      ease: [0.25, 1, 0.5, 1]
    }} className="fixed inset-0 h-full w-full object-cover origin-[50%_35%]" data-id="element-394" data-mp="3hbmswkjwsx2x" />

      {/* 2. Landing Backgrounds */}
      <AnimatePresence data-id="element-395" data-mp="dvfy73jh2a6a">
        {isVideoEnded && <>
            {/* Portrait garden composition — mobile landing only */}
            <motion.img src="https://cdn.magicpatterns.com/uploads/cHU3Nz4iavCN56v6111nqP/file_00000000b9f881f68da0271c5ae2ff7b.png" alt="Websites by Mandy garden lounge" initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          ease: [0.25, 1, 0.5, 1]
        }} className="fixed inset-0 z-10 h-full w-full object-cover sm:hidden" data-id="element-396" data-mp="hy2p928bjr2f" />
            {/* Existing drive-in composition — desktop remains unchanged */}
            <motion.img src="https://cdn.magicpatterns.com/uploads/9v5L4wLa8roanet7cQpHcD/image.png" alt="Crisp view of the drive-in screen" initial={{
          opacity: 0,
          scale: 0.85
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 2,
          ease: [0.25, 1, 0.5, 1]
        }} className="fixed inset-0 z-10 hidden h-full w-full origin-center object-cover sm:block" data-id="element-397" data-mp="2nwiqqmvw6qdv" />
          </>}
      </AnimatePresence>

      {/* 3. The TV Screen Container (Exactly matches the wooden frame in the image) */}
      <AnimatePresence data-id="element-398" data-mp="1ccwvy61igm8h">
        {isVideoEnded && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8,
        duration: 1
      }}
      // On desktop, this perfectly positions the div over the wooden screen.
      // On mobile, when active, it breaks out to fill the entire screen for usability.
      // When inactive on mobile, it centers perfectly over the object-contain image.
      className={`transition-all duration-700 overflow-hidden ${isScreenActive ? 'fixed inset-0 z-50 w-full h-full max-w-none translate-x-0 translate-y-0 bg-zinc-950 sm:absolute sm:left-1/2 sm:top-1/2 sm:z-20 sm:w-[82vw] sm:max-w-[1200px] sm:-translate-x-1/2 sm:-translate-y-[56%] sm:aspect-[1.75/1] sm:shadow-[0_0_100px_rgba(0,0,0,0.8)]' : 'absolute left-1/2 top-1/2 z-20 w-[100vw] sm:w-[82vw] max-w-[1200px] -translate-x-1/2 -translate-y-[56%] aspect-[1.75/1] bg-transparent shadow-none'}`} data-id="element-399" data-mp="3nxw2iclx7box">
            <AnimatePresence mode="wait" data-id="element-400" data-mp="9qu0mykoxy60">
              {tourProject ? <InScreenTour key="tour" project={tourProject} onClose={() => setTourProject(null)} data-id="element-401" data-mp="4rhd0blzwn2d" /> : liveProject ? <InScreenLivePreview key="live" project={liveProject} onClose={() => setLiveProject(null)} data-id="element-402" data-mp="3vwr0nwmiop8t" /> : showMenu ? <InScreenMenu key="menu" onSelectTour={setTourProject} onSelectLive={setLiveProject} onClose={() => setShowMenu(false)} data-id="element-403" data-mp="3r0s3bjj7nww9" /> : <motion.div key="home-buttons" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            delay: 1.5,
            duration: 0.8
          }} className="absolute top-[47%] left-0 right-0 flex items-center justify-center gap-2 px-3 sm:top-[62%] sm:gap-4 sm:px-4" data-id="element-404" data-mp="3i5cclbdr5xuw">
                  <button onClick={() => setShowMenu(true)} className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-white/20 bg-black/40 px-3 py-2 sm:px-6 sm:py-3 text-[10px] sm:text-sm font-bold text-white shadow-lg backdrop-blur-md transition-all hover:scale-105 hover:bg-black/60 whitespace-nowrap" data-id="element-405" data-mp="2z8b59jwq7lns">
                    <MonitorIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" data-id="element-406" data-mp="1soqc6mzmuv0y" /> View Portfolio
                  </button>
                  <button onClick={() => setShowContact(true)} className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-amber-500 px-3 py-2 sm:px-6 sm:py-3 text-[10px] sm:text-sm font-bold text-white shadow-[0_0_20px_rgba(245,158,11,0.4)] transition-all hover:scale-105 hover:bg-amber-600 whitespace-nowrap" data-id="element-407" data-mp="uzev7sfpn0mu">
                    <SparklesIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" data-id="element-408" data-mp="cagvmf1muako" /> Get Yours Today
                  </button>
                </motion.div>}
            </AnimatePresence>
          </motion.div>}
      </AnimatePresence>

      {/* Top Navigation Menu */}
      <AnimatePresence data-id="element-409" data-mp="56qxmg8api1g">
        {isVideoEnded && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5,
        duration: 0.8
      }} className="absolute top-4 left-0 right-0 z-[500] hidden justify-center px-4 sm:flex" data-id="element-410" data-mp="55z89n5akgjm">
            <nav className="flex items-center gap-6 rounded-full border border-white/10 bg-black/50 px-6 py-3 backdrop-blur-md shadow-lg" data-id="element-411" data-mp="3mg74g5o2f4kb">
              <button onClick={goHome} className="shrink-0 text-[10px] sm:text-xs font-bold text-white/80 hover:text-amber-400 transition-colors uppercase tracking-widest" data-id="element-412" data-mp="8fok7ptrbka">Home</button>
              <button onClick={() => {
            goHome();
            setShowMenu(true);
          }} className="shrink-0 text-[10px] sm:text-xs font-bold text-white/80 hover:text-amber-400 transition-colors uppercase tracking-widest" data-id="element-413" data-mp="1bnvwp1neav79">Portfolio</button>
              <button onClick={() => {
            goHome();
            setShowPricing(true);
          }} className="shrink-0 text-[10px] sm:text-xs font-bold text-white/80 hover:text-amber-400 transition-colors uppercase tracking-widest" data-id="element-414" data-mp="2olwe31dqoazf">Pricing</button>
              <button onClick={() => {
            goHome();
            setShowAbout(true);
          }} className="shrink-0 text-[10px] sm:text-xs font-bold text-white/80 hover:text-amber-400 transition-colors uppercase tracking-widest" data-id="element-415" data-mp="2fmrersucsdm7">About</button>
              <button onClick={() => {
            goHome();
            setShowContact(true);
          }} className="shrink-0 text-[10px] sm:text-xs font-bold text-white/80 hover:text-amber-400 transition-colors uppercase tracking-widest" data-id="element-416" data-mp="38ax0aht8e6n3">Contact</button>
            </nav>
          </motion.div>}
      </AnimatePresence>

      {/* Mobile Navigation — kept at the top so the landing artwork stays clear */}
      <AnimatePresence data-id="element-417" data-mp="3r8hgl0hxjww3">
        {isVideoEnded && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.25,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute right-4 top-4 z-[600] sm:hidden" data-id="element-418" data-mp="3vzzj3szlq5j6">
            <button type="button" onClick={() => setIsMobileNavOpen(isOpen => !isOpen)} aria-expanded={isMobileNavOpen} aria-controls="mobile-site-navigation" className="inline-flex size-11 items-center justify-center rounded-full border border-white/35 bg-black/55 text-white shadow-lg backdrop-blur-md transition-colors hover:bg-black/70" aria-label={isMobileNavOpen ? "Close navigation menu" : "Open navigation menu"} data-id="element-419" data-mp="1m7wyb6kclrx1">
              {isMobileNavOpen ? <XIcon size={20} data-id="element-420" data-mp="20vrskre084c8" /> : <MenuIcon size={21} data-id="element-421" data-mp="20wjm7wo1jrzv" />}
            </button>

            <AnimatePresence data-id="element-422" data-mp="tjrd6ipdu0wo">
              {isMobileNavOpen && <motion.nav id="mobile-site-navigation" initial={{
            opacity: 0,
            y: -8,
            scale: 0.96
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -8,
            scale: 0.96
          }} transition={{
            duration: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} aria-label="Mobile navigation" className="absolute right-0 top-14 flex w-40 flex-col overflow-hidden rounded-2xl border border-white/20 bg-black/75 p-1.5 shadow-2xl backdrop-blur-md" data-id="element-423" data-mp="tgwtite70k4b">
                  <button onClick={goHome} className="rounded-xl px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-white/10" data-id="element-424" data-mp="3juzhn7c71nbh">Home</button>
                  <button onClick={() => {
              goHome();
              setShowMenu(true);
            }} className="rounded-xl px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-white/10" data-id="element-425" data-mp="3swp5k36va44i">Portfolio</button>
                  <button onClick={() => {
              goHome();
              setShowPricing(true);
            }} className="rounded-xl px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-white/10" data-id="element-426" data-mp="1ykisszybrfa3">Pricing</button>
                  <button onClick={() => {
              goHome();
              setShowAbout(true);
            }} className="rounded-xl px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-white/10" data-id="element-427" data-mp="1tu9gy07diuym">About</button>
                  <button onClick={() => {
              goHome();
              setShowContact(true);
            }} className="rounded-xl bg-amber-500 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-amber-600" data-id="element-428" data-mp="1k4pwwr7xcv26">Contact</button>
                </motion.nav>}
            </AnimatePresence>
          </motion.div>}
      </AnimatePresence>

      {/* 4. Footer Links (Terms & Privacy) */}
      <AnimatePresence data-id="element-429" data-mp="22smhjp1v8ehc">
        {isVideoEnded && !isScreenActive && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        delay: 2,
        duration: 1
      }} className="absolute bottom-8 sm:bottom-4 left-0 right-0 z-20 flex flex-col items-center gap-2 px-4" data-id="element-430" data-mp="ncm9xkw91f52">
            <div className="flex items-center gap-4 [text-shadow:0_1px_2px_rgba(0,0,0,1),0_0_8px_rgba(0,0,0,0.9)]" data-id="element-431" data-mp="evvfecipsorq">
              <button onClick={() => setShowTerms(true)} className="text-xs font-bold text-white transition-colors hover:text-white/80 hover:underline" data-id="element-432" data-mp="ttfitezyqbvs">
                Terms & Conditions
              </button>
              <span className="text-xs font-bold text-white/70" data-id="element-433" data-mp="3sc33y9enda6t">•</span>
              <button onClick={() => setShowPrivacy(true)} className="text-xs font-bold text-white transition-colors hover:text-white/80 hover:underline" data-id="element-434" data-mp="av34zfy3wdtv">
                Privacy Policy
              </button>
            </div>
            <a href="https://trippinwithmandy.com" target="_blank" rel="noopener noreferrer" className="text-[10px] font-bold text-white text-center transition-colors hover:text-white/80 hover:underline [text-shadow:0_1px_2px_rgba(0,0,0,1),0_0_8px_rgba(0,0,0,0.9)]" data-id="element-435" data-mp="3eibybc41eagd">
              Made with love and a whole lot of grit by Trippin' With Mandy™ LLC. © 2026
            </a>
          </motion.div>}
      </AnimatePresence>

      {/* 5. Shared Background Video for About & Contact Pages */}
      <AnimatePresence data-id="element-436" data-mp="3gbekbocfez7c">
        {showSharedVideo && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.5
      }} className="fixed inset-0 z-[350] bg-black" data-id="element-437" data-mp="3kbyj0wcneavl">
            <video src="https://cdn.magicpatterns.com/uploads/wAVEWvFZneY42MFBT2gmfB/lv_0_20260816011659.mp4" autoPlay loop muted playsInline className="absolute inset-0 h-full w-full object-cover" data-id="element-438" data-mp="3ub5t9ao35peh" />
            {/* Subtle overlay to ensure text readability against the bright video */}
            <div className="absolute inset-0 bg-black/50" data-id="element-439" data-mp="2p2l42q7e82cw" />
          </motion.div>}
      </AnimatePresence>

      {/* 6. Modals (Appear over everything) */}
      <AnimatePresence data-id="element-440" data-mp="2k9s3meiyjg6m">
        {showContact && <ContactDialog onClose={() => setShowContact(false)} data-id="element-441" data-mp="2fc09reyr5jzs" />}
        {showTerms && <TermsDialog onClose={() => setShowTerms(false)} data-id="element-442" data-mp="2bb6j4dtxdr37" />}
        {showPrivacy && <PrivacyDialog onClose={() => setShowPrivacy(false)} data-id="element-443" data-mp="2b93o1fdx17rm" />}
        {showAbout && <AboutDialog onClose={() => setShowAbout(false)} data-id="element-444" data-mp="26b24ktckv6fm" />}
        {showPricing && <PricingDialog onClose={() => setShowPricing(false)} data-id="element-445" data-mp="21e1w6bs99rlc" />}
      </AnimatePresence>

    </main>;
}